import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventsApi, registrationsApi, PublicEvent, PublicSponsor, SponsorTier, sponsorLogoSrc } from '../api/client';
import { CalendarIcon, MapPinIcon, CheckCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';
import TicketRequestForm, { TicketFormData } from '../components/TicketRequestForm';
import CasualTicketRequestForm, { CasualTicketFormData } from '../components/CasualTicketRequestForm';
import { formatEventDateTime } from '../utils/datetime';
import { handleApiError } from '../utils/errorHandler';

// Logo heights by sponsor tier: small beside the title, larger in the footer strip
const SPONSOR_TOP_CLASS: Record<SponsorTier, string> = { headline: 'h-14', partner: 'h-8', supporter: 'h-6' };
const SPONSOR_FOOTER_CLASS: Record<SponsorTier, string> = { headline: 'h-16', partner: 'h-10', supporter: 'h-7' };

const SponsorLogo: React.FC<{ sponsor: PublicSponsor; className: string }> = ({ sponsor, className }) => {
  const img = (
    <img
      src={sponsorLogoSrc(sponsor.logo_url)}
      alt={sponsor.name}
      className={`${className} object-contain`}
    />
  );
  return sponsor.website_url ? (
    <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer" title={sponsor.name}>
      {img}
    </a>
  ) : img;
};

const EventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<TicketFormData>({
    given_name: '',
    family_name: '',
    pronouns: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    linkedin_url: '',
    relationship_type: '',
    dietary_allergies: '',
    dietary_preferences: '',
    non_dietary_requirements: '',
    how_heard: '',
    code_of_conduct_accepted: false,
    privacy_policy_accepted: false,
    entry_requirement_accepted: false,
    newsletter_opt_in: false
  });

  const [casualFormData, setCasualFormData] = useState<CasualTicketFormData>({
    name: '',
    email: '',
    company: '',
    code_of_conduct_accepted: false,
    privacy_policy_accepted: false,
    entry_requirement_accepted: false,
    newsletter_opt_in: false
  });

  useEffect(() => {
    if (id) loadEvent(parseInt(id));
  }, [id]);

  const loadEvent = async (eventId: number) => {
    try {
      const data = await eventsApi.getPublicEvent(eventId);
      setEvent(data);
    } catch (error) {
      console.error('Failed to load event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;

    setRequesting(true);
    setError('');

    try {
      // Get reCAPTCHA token
      let token = '';
      const grecaptcha = (window as any).grecaptcha;
      if (grecaptcha) {
        try {
          token = await grecaptcha.execute('6Lfb2p8rAAAAAGfH73U2e-K8gprxrnJumqQOkIDc', {
            action: 'ticket_request'
          });
        } catch (err) {
          console.warn('reCAPTCHA failed, proceeding without it:', err);
        }
      }

      // Handle different form types based on event type
      if (event.event_type === 'casual') {
        // For casual events, use simplified form data
        const names = casualFormData.name.trim().split(' ');
        await registrationsApi.requestRegistration(event.id, {
          given_name: names[0] || casualFormData.name,
          family_name: names.slice(1).join(' ') || '',
          pronouns: '',
          email: casualFormData.email,
          company: casualFormData.company || '',
          role: '',
          relationship_type: 'prospective_client',
          dietary_requirements: '',
          non_dietary_requirements: '',
          how_heard: '',
          code_of_conduct_accepted: casualFormData.code_of_conduct_accepted,
          privacy_policy_accepted: casualFormData.privacy_policy_accepted,
          entry_requirement_accepted: !!casualFormData.entry_requirement_accepted,
          newsletter_opt_in: casualFormData.newsletter_opt_in,
          recaptcha_token: token
        });
      } else {
        // For other event types, use full form data
        let dietaryRequirements = '';
        const parts = [];
        if (formData.dietary_allergies) {
          parts.push(`Allergies/Intolerances: ${formData.dietary_allergies}`);
        }
        if (formData.dietary_preferences) {
          parts.push(`Dietary preferences: ${formData.dietary_preferences}`);
        }
        dietaryRequirements = parts.join('; ');

        await registrationsApi.requestRegistration(event.id, {
          given_name: formData.given_name,
          family_name: formData.family_name,
          pronouns: formData.pronouns,
          email: formData.email,
          company: formData.company,
          role: formData.role,
          phone: formData.phone,
          linkedin_url: formData.linkedin_url,
          relationship_type: formData.relationship_type,
          dietary_requirements: dietaryRequirements,
          non_dietary_requirements: formData.non_dietary_requirements,
          how_heard: formData.how_heard,
          code_of_conduct_accepted: formData.code_of_conduct_accepted,
          privacy_policy_accepted: formData.privacy_policy_accepted,
          entry_requirement_accepted: !!formData.entry_requirement_accepted,
          newsletter_opt_in: formData.newsletter_opt_in,
          recaptcha_token: token
        });
      }
      setSuccess(true);
      setSuccessMessage(''); // Use default message for new registration
    } catch (error) {
      const apiError = handleApiError(error);
      const errorMessage = apiError.detail || apiError.message;
      
      // Handle 409 status (duplicate registration with different states)
      if (apiError.status === 409) {
        // Check if this is a success case (email resent)
        if (errorMessage.includes('has been sent') || errorMessage.includes('has been resent')) {
          // Show as success with the specific message
          setSuccess(true);
          setSuccessMessage(errorMessage);
          setError(''); // Clear any error
          return;
        }
      }
      
      setError(errorMessage);
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Event not found.</p>
        <Link to="/" className="text-blue-600 hover:underline mt-4 inline-block">
          Back to events
        </Link>
      </div>
    );
  }

  const isPastEvent = new Date(event.date_time) < new Date();

  // Where a public registration goes - falls back to the old spots logic if the
  // API predates admission policies
  const registrationState =
    event.registration_state ?? (event.available_spots > 0 ? 'open' : 'waitlist');

  const sponsors: PublicSponsor[] = event.sponsors || [];

  const submitButtonText =
    registrationState === 'review'
      ? 'Request an invitation'
      : registrationState === 'waitlist'
      ? 'Join the waitlist'
      : event.event_type === 'casual'
      ? 'Register'
      : 'Submit Request';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Logo size="xlarge" />
            <nav className="flex space-x-4">
              <Link
                to="/"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                ← Back to events
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <div className="bg-white rounded-lg shadow-lg p-8 overflow-hidden">
        {/* Brand accent strip */}
        <div className="h-1.5 -mx-8 -mt-8 mb-6 bg-gradient-to-r from-[#0F2A47] via-[#1B7FF2] to-[#0F2A47]"></div>
        <div className="flex items-start justify-between gap-6 flex-wrap mb-4">
          <h1 className="text-3xl font-bold text-[#0F2A47]">{event.title}</h1>
          {sponsors.length > 0 && (
            <div className="flex items-center gap-5 pt-1.5">
              {sponsors.map((sponsor) => (
                <SponsorLogo key={sponsor.name} sponsor={sponsor} className={SPONSOR_TOP_CLASS[sponsor.tier]} />
              ))}
            </div>
          )}
        </div>
        
        {event.description && (
          <p className="text-gray-600 mb-6 whitespace-pre-wrap">{event.description}</p>
        )}

        <div className="space-y-3 mb-8">
          <div className="flex items-center text-gray-700">
            <CalendarIcon className="h-5 w-5 mr-3 text-[#1B7FF2]" />
            <span>{formatEventDateTime(event.date_time, event.timezone || 'Europe/London')}</span>
          </div>
          
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
          >
            <MapPinIcon className="h-5 w-5 mr-3 text-[#1B7FF2]" />
            <span>{event.venue}</span>
          </a>
        </div>
        
        <div className="flex items-center justify-center mb-8">
          <a
            href={`/api/events/public/${event.id}/calendar`}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <CalendarDaysIcon className="h-5 w-5 mr-2" />
            Add to Calendar
          </a>
        </div>

        {/* Constellation divider — echoes the Star hub-and-spoke mark */}
        <div className="flex justify-center mb-8" aria-hidden="true">
          <svg width="280" height="16" viewBox="0 0 280 16">
            <line x1="0" y1="8" x2="280" y2="8" stroke="#C9D7E6" strokeWidth="1" />
            <circle cx="6" cy="8" r="2.5" fill="#0F2A47" />
            <circle cx="70" cy="8" r="3.5" fill="#1B7FF2" />
            <circle cx="120" cy="8" r="2" fill="#DD5257" />
            <circle cx="140" cy="8" r="4.5" fill="#1B7FF2" />
            <circle cx="160" cy="8" r="2" fill="#35A05A" />
            <circle cx="210" cy="8" r="3.5" fill="#1B7FF2" />
            <circle cx="274" cy="8" r="2.5" fill="#0F2A47" />
          </svg>
        </div>

        {success ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <CheckCircleIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            {successMessage ? (
              // Custom success message for resent emails or other states
              <>
                <h2 className="text-xl font-semibold text-blue-900 mb-2">
                  Action Completed
                </h2>
                <p className="text-blue-700">
                  {successMessage}
                </p>
              </>
            ) : (
              // Default message for new registrations
              <>
                <h2 className="text-xl font-semibold text-red-600 mb-2">
                  ⚠️ ACTION REQUIRED: Verify Your Email
                </h2>
                <p className="text-gray-700 mb-3">
                  We've sent a verification email to <strong>{formData.email}</strong>.
                </p>
                <div className="mt-3 p-4 bg-red-50 border-2 border-red-300 rounded">
                  <p className="text-red-800 font-semibold mb-2">
                    Your ticket request is NOT complete yet!
                  </p>
                  <p className="text-red-700">
                    You MUST click the verification link in the email to complete your registration.
                  </p>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  <strong>Important:</strong> 
                  <ul className="mt-1 ml-4 list-disc">
                    <li>Check your spam/junk folder if you don't see the email</li>
                    <li>The verification link expires in 24 hours</li>
                    <li>Your ticket request will be cancelled if not verified</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {event.status === 'archived' ? (
              <div className="text-center py-8">
                <p className="text-gray-600 text-lg">
                  This event is no longer accepting registrations.
                </p>
              </div>
            ) : isPastEvent ? (
              <div className="text-center py-8">
                <p className="text-gray-600 text-lg">
                  This event has ended.
                </p>
              </div>
            ) : registrationState === 'closed' ? (
              <div className="text-center py-8">
                <p className="text-gray-600 text-lg">
                  Registration for this event has closed.
                </p>
              </div>
            ) : (
              <>
                {registrationState === 'review' ? (
                  <div className="text-center mb-6">
                    <p className="text-lg text-gray-700 font-medium">Request an invitation</p>
                    <p className="text-sm text-gray-500 mt-1">
                      The event team personally reviews every request.
                    </p>
                    {event.at_capacity && (
                      <p className="text-sm text-gray-500 mt-1">
                        We're near capacity, but requests are still welcome.
                      </p>
                    )}
                  </div>
                ) : registrationState === 'waitlist' ? (
                  <div className="text-center mb-6">
                    <p className="text-lg text-gray-700 font-medium">This event is full — join the waitlist</p>
                    <p className="text-sm text-gray-500 mt-1">
                      We'll email you if a spot opens up.
                    </p>
                  </div>
                ) : (
                  <p className="text-lg text-gray-700 mb-6 text-center">
                    {event.available_spots} spots available
                  </p>
                )}
                {event.event_type === 'casual' ? (
                  <CasualTicketRequestForm
                    formData={casualFormData}
                    onChange={setCasualFormData}
                    onSubmit={handleRequestTicket}
                    submitButtonText={submitButtonText}
                    isSubmitting={requesting}
                    showPrivacyPolicy={true}
                    autoFocus={true}
                    entryRequirement={event.entry_requirement}
                  />
                ) : (
                  <TicketRequestForm
                    formData={formData}
                    onChange={setFormData}
                    onSubmit={handleRequestTicket}
                    submitButtonText={submitButtonText}
                    isSubmitting={requesting}
                    showPrivacyPolicy={true}
                    autoFocus={true}
                    entryRequirement={event.entry_requirement}
                  />
                )}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 mt-4">
                    <strong>Error:</strong> {error}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {sponsors.length > 0 && (
          <div className="mt-10 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs font-semibold tracking-[0.25em] text-gray-400 uppercase mb-5">
              In association with
            </p>
            <div className="flex items-center justify-center gap-10 flex-wrap">
              {sponsors.map((sponsor) => (
                <SponsorLogo key={sponsor.name} sponsor={sponsor} className={SPONSOR_FOOTER_CLASS[sponsor.tier]} />
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default EventPage;